import { Outlet, Link, useNavigate } from 'react-router-dom';
import { Search, Ticket, User, LogOut } from 'lucide-react';
import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { usePortalConfigStore } from '@/stores/portalConfigStore';
import { ChatWidget } from '@/components/ChatWidget';
import { ChatWidgetLoader } from '@/components/ChatWidgetLoader';
import { NotificationBell } from '@/components/NotificationBell';
import { DarkModeToggle } from '@/components/DarkModeToggle';

export function PortalLayout() {
  const [searchQuery, setSearchQuery] = useState('');
  // The portal's own chat component, kept as a fallback for the (rare) case
  // where the shared widget bundle is missing from this image.
  const [widgetUnavailable, setWidgetUnavailable] = useState(false);
  const navigate = useNavigate();
  const { customer, isAuthenticated, logout } = useAuthStore();
  const { config } = usePortalConfigStore();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header
        className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50"
        style={config.headerColor ? { backgroundColor: config.headerColor } : undefined}
      >
        <div className="container">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              {config.logoUrl ? (
                <img
                  src={config.logoUrl}
                  alt={config.portalName}
                  className="h-8"
                />
              ) : (
                <img
                  src="/assets/logos/logo-color.png"
                  alt={config.portalName}
                  className="h-8"
                />
              )}
            </Link>

            {/* Search Bar */}
            {config.enableSearch && (
              <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-lg mx-8">
                <div className="relative w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search for help..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </form>
            )}

            {/* Navigation */}
            <nav className="flex items-center gap-4">
              {config.enableTicketSubmission && (
                <Link
                  to="/submit-ticket"
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  <Ticket className="h-4 w-4" />
                  <span className="hidden sm:inline">Submit a Request</span>
                </Link>
              )}

              {isAuthenticated ? (
                <div className="flex items-center gap-4">
                  {config.enableTicketTracking && (
                    <Link
                      to="/my-tickets"
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    >
                      <Ticket className="h-4 w-4" />
                      <span className="hidden sm:inline">My Tickets</span>
                    </Link>
                  )}
                  <Link
                    to="/profile"
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    <User className="h-4 w-4" />
                    <span className="hidden sm:inline">{customer?.name || 'Profile'}</span>
                  </Link>
                  <NotificationBell />
                  <DarkModeToggle />
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-4">
                  <DarkModeToggle />
                  <Link
                    to="/login"
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                    style={config.primaryColor ? { backgroundColor: config.primaryColor } : undefined}
                  >
                    Sign In
                  </Link>
                </div>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Mobile Search */}
      {config.enableSearch && (
        <div className="md:hidden bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
          <form onSubmit={handleSearch}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search for help..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </form>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer
        className="bg-gray-900 dark:bg-gray-950 text-gray-400 py-12 dark:text-gray-500"
        style={config.footerColor ? { backgroundColor: config.footerColor } : undefined}
      >
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-white font-semibold mb-4">{config.portalName}</h3>
              <p className="text-sm">
                {config.footerText || 'Find answers to common questions and get help with your account.'}
              </p>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/" className="hover:text-white transition-colors">
                    Home
                  </Link>
                </li>
                {config.enableTicketSubmission && (
                  <li>
                    <Link to="/submit-ticket" className="hover:text-white transition-colors">
                      Submit a Request
                    </Link>
                  </li>
                )}
                {config.enableTicketTracking && (
                  <li>
                    <Link to="/my-tickets" className="hover:text-white transition-colors">
                      My Tickets
                    </Link>
                  </li>
                )}
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-sm">
                {config.enableContactForm && (
                  <li>
                    <Link to="/contact" className="hover:text-white transition-colors">
                      Contact Us
                    </Link>
                  </li>
                )}
                <li>
                  <a href="mailto:support@example.com" className="hover:text-white transition-colors">
                    Email Support
                  </a>
                </li>
                <li>
                  <a href="tel:+1234567890" className="hover:text-white transition-colors">
                    Phone Support
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Terms of Service
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} {config.portalName}. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Chat Widget — only if enabled AND we know the real tenant. Passing the
          portal's tenantId is required: without it the widget fell back to a
          placeholder tenant (11111111-…), so the chat WebSocket authenticated
          against the wrong tenant and join_session failed on every session,
          leaving the widget stuck on "Connecting" (#9664). Guarded on tenantId
          so we never pass undefined (which would override the default and break
          auth the same way). */}
      {/* One chat window: the portal runs the same widget customers embed on
          their own websites. If that bundle cannot be loaded from this image,
          the portal's own chat component takes over. */}
      {config.enableChat && config.tenantId && !widgetUnavailable && (
        <ChatWidgetLoader
          tenantId={config.tenantId}
          welcomeMessage={config.welcomeMessage}
          primaryColor={config.primaryColor}
          onUnavailable={() => setWidgetUnavailable(true)}
        />
      )}
      {config.enableChat && config.tenantId && widgetUnavailable && (
        <ChatWidget
          config={{
            tenantId: config.tenantId,
            ...(config.welcomeMessage ? { welcomeMessage: config.welcomeMessage } : {}),
          }}
        />
      )}
    </div>
  );
}
